import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import api from '@/lib/api';
import { AlertCircle, Stethoscope, User, Phone, ShieldCheck, CheckCircle2, Loader2, Eye, EyeOff, Building2, BadgeCheck, MapPin, Briefcase } from 'lucide-react';

const DOCTOR_CONSENT = "I confirm that all information I provide on my DocSite profile \u2014 including qualifications, experience, procedures, and any claims \u2014 is accurate and truthful. I understand that DocSite does not verify this information and I am solely responsible for its accuracy. Any claims or liabilities due to wrong representation or faulty treatments are not the liability of DocSite under any circumstances legally.";
const RMP_NOTICE = "This platform is exclusively for Registered Medical Practitioners (RMPs) holding valid, active registration with the National Medical Commission (NMC) or State Medical Councils. Only practitioners with MBBS, postgraduate medical degrees, or authorized AYUSH qualifications may use this platform. Accounts found with incorrect Registration IDs will be terminated.";
const PATIENT_CONSENT = "I understand that doctor profiles on DocSite contain self-declared information that has not been independently verified. I take full responsibility for my own research, verification, and decisions regarding the medical professionals I choose to consult through this platform.";

export default function RegisterPage() {
  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const connectToSlug = (searchParams.get('connect_to') || '').trim();
  const asParam = (searchParams.get('as') || '').trim().toLowerCase();
  const signupSource = (searchParams.get('from') || '').trim().slice(0, 64);
  // Pre-fill phone when funnel'd from /c/:slug Connect thank-you screen.
  const prefillPhone = (searchParams.get('phone') || '').replace(/[^0-9+ ]/g, '').slice(0, 20);
  // When linked from the patient preview funnel (?as=patient), lock the Doctor tab.
  const lockToPatient = asParam === 'patient' || !!connectToSlug;
  // When linked from a doctor-facing CTA (?as=doctor), lock the Patient tab.
  const lockToDoctor = asParam === 'doctor' && !connectToSlug;
  const [connectToClinic, setConnectToClinic] = useState(null);

  // Redirect logged-in users away from register page
  useEffect(() => {
    if (!authLoading && user) {
      if (user.role === 'doctor') navigate('/dashboard', { replace: true });
      else if (user.role === 'patient') navigate('/my-clinics', { replace: true });
      else navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Zero-Touch: fetch clinic card when `?connect_to=<slug>` is present
  useEffect(() => {
    if (!connectToSlug) return;
    api.get(`/public/clinic-card/${connectToSlug}`).then(r => setConnectToClinic(r.data)).catch(() => setConnectToClinic(null));
  }, [connectToSlug]);
  const [role, setRole] = useState(lockToPatient ? 'patient' : 'doctor');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(prefillPhone);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Doctor-only credentialing fields collected upfront so the registered
  // profile already has 'years of practice' + RMP + city + specialisation
  // before the dashboard opens.
  const [yearsOfPractice, setYearsOfPractice] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [city, setCity] = useState('');
  const [primarySpecialisation, setPrimarySpecialisation] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [devOtp, setDevOtp] = useState('');

  // Consent state
  const [consent, setConsent] = useState(false);

  const handleSendOtp = async () => {
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setOtpError('Enter a valid phone number (at least 10 digits)');
      return;
    }
    setOtpError('');
    setOtpLoading(true);
    try {
      const { data } = await api.post('/auth/send-otp', { phone: phone.trim() });
      setOtpToken(data.otp_token);
      setOtpSent(true);
      if (data._dev_otp) setDevOtp(data._dev_otp);
    } catch (err) {
      setOtpError(formatApiError(err.response?.data?.detail) || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) {
      setOtpError('Enter the 4-digit OTP code');
      return;
    }
    setOtpError('');
    setOtpLoading(true);
    try {
      await api.post('/auth/verify-otp', { otp_token: otpToken, code: otpCode });
      setOtpVerified(true);
      setOtpError('');
    } catch (err) {
      setOtpError(formatApiError(err.response?.data?.detail) || 'OTP verification failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (!consent) { setError('You must accept the terms and consent to register'); return; }
    if (role === 'doctor' && !otpVerified) { setError('Please verify your phone number via OTP first'); return; }
    if (role === 'doctor') {
      const yrs = parseInt(yearsOfPractice, 10);
      if (Number.isNaN(yrs) || yrs < 0 || yrs > 70) { setError('Enter your years of practice (0–70)'); return; }
      if (!registrationNumber.trim()) { setError('NMC / State Medical Council registration number is required'); return; }
      if (!city.trim()) { setError('City of practice is required'); return; }
      if (!primarySpecialisation.trim()) { setError('Pick your primary specialisation'); return; }
    }
    setLoading(true);
    try {
      const doctorExtras = role === 'doctor' ? {
        years_of_practice: parseInt(yearsOfPractice, 10),
        registration_number: registrationNumber.trim(),
        city: city.trim(),
        primary_specialisation: primarySpecialisation.trim(),
      } : null;
      await register(
        name, email, password,
        phone || undefined,
        role,
        consent,
        role === 'doctor' ? otpToken : undefined,
        role === 'patient' ? (connectToSlug || null) : null,
        signupSource || null,
        doctorExtras
      );
      navigate(role === 'doctor' ? '/dashboard' : '/');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const consentText = role === 'doctor' ? DOCTOR_CONSENT : PATIENT_CONSENT;
  const isDoctor = role === 'doctor';

  return (
    <div className="min-h-screen bg-slate-50/50 flex items-center justify-center px-6 py-12" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/docsite-logo.png" alt="DocSite" className="w-24 h-24 object-contain mx-auto mb-4" />
          <h1 className="text-2xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Get Started</h1>
          <p className="text-sm text-slate-500 mt-1">Create your account</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
          {/* Zero-Touch Onboarding banner — appears when a patient registers via a doctor's QR/share link. */}
          {connectToSlug && connectToClinic && (
            <div className="mb-5 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-sky-50 p-4 flex items-start gap-3" data-testid="connect-to-banner">
              <div className="w-10 h-10 rounded-xl bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
                <Building2 size={16} className="text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">You&apos;re connecting to</p>
                <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {connectToClinic.doctor_name}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {connectToClinic.clinic_name || connectToClinic.city}
                  {connectToClinic.city ? ` · ${connectToClinic.city}` : ''}
                </p>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Sign up and we&apos;ll auto-save this clinic to your <b>My Trusted Medical Clinics</b>. One tap to book next time.
                </p>
              </div>
            </div>
          )}

          {/* Role Toggle */}
          <div className="flex gap-2 mb-2" data-testid="role-toggle">
            <button
              type="button"
              disabled={lockToPatient}
              onClick={() => {
                if (lockToPatient) return;
                setRole('doctor'); setOtpSent(false); setOtpVerified(false); setOtpCode(''); setOtpError(''); setConsent(false);
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-colors ${
                role === 'doctor'
                  ? 'bg-sky-600 text-white'
                  : lockToPatient
                    ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              title={lockToPatient ? "You're signing up from the patient preview — switch via the link below" : ''}
              data-testid="role-doctor">
              <Stethoscope size={16} /> I'm a Doctor
            </button>
            <button
              type="button"
              disabled={lockToDoctor}
              onClick={() => {
                if (lockToDoctor) return;
                setRole('patient'); setOtpSent(false); setOtpVerified(false); setOtpCode(''); setOtpError(''); setConsent(false);
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-colors ${
                role === 'patient'
                  ? 'bg-sky-600 text-white'
                  : lockToDoctor
                    ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              title={lockToDoctor ? "You're signing up from a doctor-facing page — switch via the link below" : ''}
              data-testid="role-patient">
              <User size={16} /> I'm a Patient
            </button>
          </div>
          {lockToPatient && (
            <p className="text-[11px] text-slate-400 mb-6 leading-relaxed" data-testid="lock-to-patient-hint">
              You came from the patient preview. <Link to="/register" className="text-sky-600 font-medium hover:underline" data-testid="switch-to-doctor-link">Register as a doctor instead →</Link>
            </p>
          )}
          {lockToDoctor && (
            <p className="text-[11px] text-slate-400 mb-6 leading-relaxed" data-testid="lock-to-doctor-hint">
              Signing up as a doctor. <Link to="/register?as=patient" className="text-sky-600 font-medium hover:underline" data-testid="switch-to-patient-link">Register as a patient instead →</Link>
            </p>
          )}
          {!lockToPatient && !lockToDoctor && <div className="mb-4" />}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isDoctor && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="rmp-notice">
                <ShieldCheck size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed font-medium">{RMP_NOTICE}</p>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 text-sm text-red-600" data-testid="register-error">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">{isDoctor ? 'Full Name (as shown on website)' : 'Full Name'}</label>
              <input type="text" required className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                value={name} onChange={(e) => setName(e.target.value)} placeholder={isDoctor ? 'Dr. Your Name' : 'Your full name'} data-testid="register-name-input" />
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email</label>
              <input type="email" required className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="register-email-input" />
            </div>

            {/* Phone + OTP for Doctors */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                <Phone size={12} className="inline mr-1" />
                Phone {isDoctor ? '(required for verification)' : '(optional)'}
              </label>
              <div className="flex gap-2">
                <input type="tel" required={isDoctor} className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                  value={phone} onChange={(e) => { setPhone(e.target.value); if (otpSent) { setOtpSent(false); setOtpVerified(false); setOtpCode(''); } }}
                  placeholder="+91 98765 43210" data-testid="register-phone-input" disabled={otpVerified} />
                {isDoctor && !otpVerified && (
                  <button type="button" onClick={handleSendOtp} disabled={otpLoading || !phone}
                    className="px-4 py-2.5 rounded-xl text-xs font-medium bg-slate-800 text-white hover:bg-slate-700 transition-colors disabled:opacity-40 whitespace-nowrap"
                    data-testid="send-otp-button">
                    {otpLoading && !otpSent ? <Loader2 size={14} className="animate-spin" /> : otpSent ? 'Resend' : 'Send OTP'}
                  </button>
                )}
                {otpVerified && (
                  <div className="flex items-center gap-1 px-3 py-2.5 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-medium" data-testid="otp-verified-badge">
                    <CheckCircle2 size={14} /> Verified
                  </div>
                )}
              </div>

              {/* OTP Error */}
              {otpError && (
                <p className="text-xs text-red-500 mt-1.5" data-testid="otp-error">{otpError}</p>
              )}

              {/* OTP Input (shown after send) */}
              {isDoctor && otpSent && !otpVerified && (
                <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
                  <label className="text-xs font-medium text-slate-600 block">Enter 4-digit OTP</label>
                  <div className="flex gap-2">
                    <input type="text" maxLength={4} className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm tracking-widest text-center font-mono focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                      value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="----" data-testid="otp-code-input" />
                    <button type="button" onClick={handleVerifyOtp} disabled={otpLoading || otpCode.length < 4}
                      className="px-4 py-2.5 rounded-xl text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-40"
                      data-testid="verify-otp-button">
                      {otpLoading ? <Loader2 size={14} className="animate-spin" /> : 'Verify'}
                    </button>
                  </div>
                  {devOtp && (
                    <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg" data-testid="dev-otp-hint">
                      Dev mode OTP: <span className="font-mono font-bold">{devOtp}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Doctor credentialing — collected upfront so the registered profile already has years + RMP + city + specialisation. */}
            {isDoctor && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-4 space-y-3" data-testid="doctor-credentials-block">
                <p className="text-xs font-semibold text-sky-900 flex items-center gap-1.5">
                  <BadgeCheck size={14} className="text-sky-700" /> Practice details (required)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 mb-1 block flex items-center gap-1">
                      <Briefcase size={11} /> Years of practice
                    </label>
                    <input type="number" min="0" max="70" required
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                      value={yearsOfPractice} onChange={(e) => setYearsOfPractice(e.target.value.replace(/\D/g, '').slice(0, 2))}
                      placeholder="e.g. 12" data-testid="register-years-input" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-600 mb-1 block flex items-center gap-1">
                      <MapPin size={11} /> City of practice
                    </label>
                    <input type="text" required maxLength={64}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                      value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Mumbai" data-testid="register-city-input" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-600 mb-1 block flex items-center gap-1">
                    <ShieldCheck size={11} /> NMC / State Medical Council Registration No.
                  </label>
                  <input type="text" required maxLength={48}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                    value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} placeholder="e.g. MMC/2018/12345" data-testid="register-rmp-input" />
                  <p className="text-[10px] text-slate-500 mt-1">We verify this on every Gold-plan account. Wrong numbers are terminated.</p>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-600 mb-1 block">Primary specialisation</label>
                  <select required value={primarySpecialisation} onChange={(e) => setPrimarySpecialisation(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                    data-testid="register-specialisation-select">
                    <option value="">Select your primary specialisation</option>
                    <option value="general_practice">General Practice / Family Physician</option>
                    <option value="cardiology">Cardiology</option>
                    <option value="ent">ENT (Otolaryngology)</option>
                    <option value="orthopaedics">Orthopaedics</option>
                    <option value="gynaecology">Gynaecology &amp; Obstetrics</option>
                    <option value="paediatrics">Paediatrics</option>
                    <option value="dermatology">Dermatology</option>
                    <option value="psychiatry">Psychiatry</option>
                    <option value="neurology">Neurology</option>
                    <option value="gastroenterology">Gastroenterology</option>
                    <option value="urology">Urology</option>
                    <option value="ophthalmology">Ophthalmology</option>
                    <option value="dentistry">Dentistry</option>
                    <option value="endocrinology">Endocrinology</option>
                    <option value="oncology">Oncology</option>
                    <option value="pulmonology">Pulmonology</option>
                    <option value="nephrology">Nephrology</option>
                    <option value="general_surgery">General Surgery</option>
                    <option value="plastic_surgery">Plastic / Cosmetic Surgery</option>
                    <option value="ayurveda">Ayurveda (BAMS)</option>
                    <option value="homeopathy">Homeopathy (BHMS)</option>
                    <option value="physiotherapy">Physiotherapy</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} required className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" tabIndex={-1}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Legal Consent */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3" data-testid="consent-section">
              <div className="flex items-start gap-3">
                <ShieldCheck size={18} className="text-sky-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-slate-700 mb-1">
                    {isDoctor ? 'Doctor Liability Declaration' : 'Patient Acknowledgement'}
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed" data-testid="consent-text">
                    {consentText}
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="consent-checkbox-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  data-testid="consent-checkbox" />
                <span className="text-xs font-medium text-slate-700">I have read, understood, and agree to the above</span>
              </label>
              <div className="flex flex-wrap gap-3 text-[10px]">
                <Link to={isDoctor ? '/terms/doctor' : '/terms/patient'} target="_blank" className="text-sky-600 hover:text-sky-700 underline" data-testid="terms-link">
                  {isDoctor ? 'Doctor' : 'Patient'} Terms & Conditions
                </Link>
                <Link to="/privacy" target="_blank" className="text-sky-600 hover:text-sky-700 underline" data-testid="privacy-link">
                  Privacy Policy
                </Link>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading || !consent || (isDoctor && !otpVerified)}
              className="w-full rounded-full py-3 bg-sky-600 text-white font-medium hover:bg-sky-700 transition-colors text-sm disabled:opacity-50"
              data-testid="register-submit-button">
              {loading ? 'Creating...' : isDoctor ? 'Create My Website' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account? <Link to="/login" className="text-sky-600 font-medium hover:text-sky-700" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
